import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Table,
  Button,
  Input,
  Modal,
  Drawer,
  Form,
  Tag,
  Space,
  message,
  Dropdown,
  Avatar,
  Typography,
  Flex,
  Select,
  Spin,
  Collapse,
  Divider,
} from "antd";
import {
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MoreOutlined,
  FilterOutlined,
  LoadingOutlined,
  SafetyCertificateOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  FilePdfOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { ApprovalProcessWrapper, PageHeader } from "./ApprovalProcess.style";
import MaskGroup from "../../../assets/Mask-Group.svg";
import axios from "axios";
import { API_CONST } from "../../../const";

const { TextArea } = Input;
const { Text} = Typography;
const { Option } = Select;


const ApprovalProcess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || "Users");
  const [searchText, setSearchText] = useState("");
  const [filterType, setFilterType] = useState(null);
  const [filterLocation, setFilterLocation] = useState(null);
  const [filterRole, setFilterRole] = useState(null);
  const [filterExperience, setFilterExperience] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [currentRejectRecord, setCurrentRejectRecord] = useState(null);
  const [form] = Form.useForm();
  const [verificationDrawerVisible, setVerificationDrawerVisible] = useState(false);
  const [verificationTalent, setVerificationTalent] = useState(null);
  const [verificationDocUrls, setVerificationDocUrls] = useState({});
  const [idVerifLoading, setIdVerifLoading] = useState(false);
  const [inlineAadhaarNum, setInlineAadhaarNum] = useState("");
  const [inlinePanNum, setInlinePanNum] = useState("");
  const [inlinePassportNum, setInlinePassportNum] = useState("");
  const [inlineVerifNotes, setInlineVerifNotes] = useState("");

  // State for each data type
  const [partnersData, setPartnersData] = useState([]);
  const [talentsData, setTalentsData] = useState([]);
  const [jobsData, setJobsData] = useState([]);
  
  // Loading states for each type
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTalents, setLoadingTalents] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  
  // Counts for each type
  const [usersCount, setUsersCount] = useState(0);
  const [talentsCount, setTalentsCount] = useState(0);
  const [jobsCount, setJobsCount] = useState(0);
  const [timesheetsCount, setTimesheetsCount] = useState(0);
  
  // State for Engagements and Projects
  const [engagementsData, setEngagementsData] = useState([]);
  const [projectsData, setProjectsData] = useState([]);
  const [loadingEngagements, setLoadingEngagements] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [engagementsCount, setEngagementsCount] = useState(0);
  const [projectsCount, setProjectsCount] = useState(0);

  // Fetch pending users (Partners/Customers) - Inactive status = pending approval
  const fetchPendingUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_USER_MANAGEMENT,
        { status: "Inactive", limit: 100 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const users = response.data.Response || [];
      setPartnersData(users.map(u => ({ 
        ...u, 
        id: u.key,
        name: u.organization || u.name,
        type: u.type || (u.role === "Partner" ? "Partner" : "Customer"),
        contactPerson: u.name,
        registeredOn: u.createdOn || (u.createdAt ? new Date(u.createdAt).toLocaleDateString("en-IN") : "-"),
        status: "Pending",
      })));
      setUsersCount(users.length);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      message.error("Failed to load pending users");
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  // Fetch pending talents (Inactive status = pending approval)
  const fetchPendingTalents = useCallback(async () => {
    setLoadingTalents(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_TALENT_PROFILES,
        { status: "Inactive", limit: 100 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const talents = response.data.Response || [];
      setTalentsData(talents.map(t => ({ 
        ...t, 
        key: t.id || t.key,
        name: t.name,
        role: t.role,
        skills: Array.isArray(t.primarySkills) ? t.primarySkills.join(", ") : (Array.isArray(t.skills) ? t.skills.map(s => s.skill || s).join(", ") : t.skills),
        experience: t.experience || t.totalExperience || "-",
        location: t.location || t.currentLocation,
        monthlyRate: (t.partnerRate || t.monthlyRate) ? `₹${Number(t.partnerRate || t.monthlyRate).toLocaleString("en-IN")}` : "-",
        partnerOrg: t.partnerOrganization || t.organization,
        submittedOn: (t.createdOn || t.createdAt) ? new Date(t.createdOn || t.createdAt).toLocaleDateString("en-IN") : "-",
        status: "Pending",
      })));
      setTalentsCount(talents.length);
    } catch (error) {
      console.error("Failed to fetch talents:", error);
      message.error("Failed to load pending talents");
    } finally {
      setLoadingTalents(false);
    }
  }, []);

  // Fetch jobs awaiting admin approval (only pending_approval status — drafts stay with the customer)
  const fetchPendingJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_JOB_REQUIREMENTS,
        { status: "pending_approval", limit: 100 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const pendingJobs = response.data.jobs || response.data.Response || [];
      setJobsData(pendingJobs.map(j => ({
        ...j,
        key: j.id,
        jobTitle: j.role || j.title || j.jobTitle,
        company: j.customerOrganization || j.company,
        experience: j.experienceRange || j.experienceRequired || j.experience,
        skills: Array.isArray(j.primarySkills) ? j.primarySkills.join(", ") : (Array.isArray(j.requiredSkills) ? j.requiredSkills.join(", ") : j.skills),
        budget: j.budget ? `₹${Number(j.budget).toLocaleString("en-IN")}` : (j.budgetMax ? `₹${Number(j.budgetMax).toLocaleString("en-IN")}` : "-"),
        positions: j.numberOfPositions || j.positions || 1,
        postedOn: j.createdAt ? new Date(j.createdAt).toLocaleDateString("en-IN") : "-",
        originalStatus: j.status,
        status: "Pending Approval",
      })));
      setJobsCount(pendingJobs.length);
    } catch (error) {
      console.error("Failed to fetch jobs:", error);
      message.error("Failed to load pending jobs");
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  // Fetch pending timesheet count for summary badge
  const fetchPendingTimesheets = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_TIMESHEETS,
        { status: "submitted", limit: 100 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const timesheets = response.data.timesheets || [];
      setTimesheetsCount(timesheets.length);
    } catch (error) {
      console.error("Failed to fetch timesheet count:", error);
    }
  }, []);

  // Fetch pending engagements
  const fetchPendingEngagements = useCallback(async () => {
    setLoadingEngagements(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_ENGAGEMENTS,
        {
          status: "pending_approval",
          page: 1,
          limit: 100,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const engagements = response.data.engagements || [];
      setEngagementsData(engagements.map(e => ({ ...e, key: e.id })));
      setEngagementsCount(engagements.length);
    } catch (error) {
      console.error("Failed to fetch engagements:", error);
      message.error("Failed to load pending engagements");
    } finally {
      setLoadingEngagements(false);
    }
  }, []);

  // Fetch pending projects
  const fetchPendingProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_PROJECTS,
        {
          status: "pending_approval",
          page: 1,
          limit: 100,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const projects = response.data.projects || [];
      setProjectsData(projects.map(p => ({ ...p, key: p.id })));
      setProjectsCount(projects.length);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
      message.error("Failed to load pending projects");
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  // Fetch all counts on initial mount
  useEffect(() => {
    fetchPendingUsers();
    fetchPendingTalents();
    fetchPendingJobs();
    fetchPendingEngagements();
    fetchPendingProjects();
  }, []);

  // Fetch data on tab change
  useEffect(() => {
    if (activeTab === "Users") {
      fetchPendingUsers();
    } else if (activeTab === "Profiles") {
      fetchPendingTalents();
    } else if (activeTab === "Jobs") {
      fetchPendingJobs();
    } else if (activeTab === "Engagements") {
      fetchPendingEngagements();
    } else if (activeTab === "Projects") {
      fetchPendingProjects();
    }
  }, [activeTab, fetchPendingUsers, fetchPendingTalents, fetchPendingJobs, fetchPendingEngagements, fetchPendingProjects]);

  // Get current data based on active tab
  const getCurrentData = () => {
    switch (activeTab) {
      case "Users":
        return partnersData;
      case "Profiles":
        return talentsData;
      case "Jobs":
        return jobsData;
      case "Engagements":
        return engagementsData;
      case "Projects":
        return projectsData;
      default:
        return [];
    }
  };

  // Get pending counts for each tab
  const getPendingCounts = () => {
    return {
      partners: usersCount,
      talents: talentsCount,
      jobs: jobsCount,
      timesheets: timesheetsCount,
      engagements: engagementsCount,
      projects: projectsCount,
    };
  };

  const pendingCounts = getPendingCounts();

  // Handle Engagement approve
  const handleEngagementApprove = async (record) => {
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_CONST.APPROVE_ENGAGEMENT}/${record.id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(`Engagement "${record.title}" approved successfully!`);
      fetchPendingEngagements();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to approve engagement");
    }
  };

  // Handle Engagement reject
  const handleEngagementReject = (record) => {
    setCurrentRejectRecord({ ...record, type: "Engagement" });
    setRejectModalVisible(true);
  };

  // Handle Project approve
  const handleProjectApprove = async (record) => {
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_CONST.APPROVE_PROJECT}/${record.id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(`Project "${record.title}" approved successfully!`);
      fetchPendingProjects();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to approve project");
    }
  };

  // Handle Project reject
  const handleProjectReject = (record) => {
    setCurrentRejectRecord({ ...record, type: "Project" });
    setRejectModalVisible(true);
  };

  // Filter data based on search and filters
  const filteredData = getCurrentData().filter((item) => {
    const searchLower = searchText.toLowerCase();
    
    // Apply search filter
    let matchesSearch = false;
    if (activeTab === "Users") {
      matchesSearch = (
        item.name.toLowerCase().includes(searchLower) ||
        item.contactPerson.toLowerCase().includes(searchLower) ||
        item.email.toLowerCase().includes(searchLower)
      );
    } else if (activeTab === "Profiles") {
      matchesSearch = (
        item.name.toLowerCase().includes(searchLower) ||
        item.role.toLowerCase().includes(searchLower) ||
        item.skills.toLowerCase().includes(searchLower)
      );
    } else if (activeTab === "Engagements") {
      matchesSearch = (
        (item.title || "").toLowerCase().includes(searchLower) ||
        (item.id || "").toLowerCase().includes(searchLower) ||
        (item.customerOrganization || "").toLowerCase().includes(searchLower)
      );
    } else if (activeTab === "Projects") {
      matchesSearch = (
        (item.title || "").toLowerCase().includes(searchLower) ||
        (item.id || "").toLowerCase().includes(searchLower) ||
        (item.customerOrganization || "").toLowerCase().includes(searchLower)
      );
    } else {
      matchesSearch = (
        item.jobTitle.toLowerCase().includes(searchLower) ||
        item.company.toLowerCase().includes(searchLower) ||
        item.skills.toLowerCase().includes(searchLower)
      );
    }

    // Apply dropdown filters
    let matchesFilters = true;
    if (activeTab === "Users") {
      matchesFilters = (!filterType || item.type === filterType) &&
                       (!filterLocation || item.location === filterLocation);
    } else if (activeTab === "Profiles") {
      matchesFilters = (!filterRole || item.role === filterRole) &&
                       (!filterLocation || item.location === filterLocation);
    } else {
      matchesFilters = (!filterLocation || item.location === filterLocation) &&
                       (!filterExperience || item.experience === filterExperience);
    }

    return matchesSearch && matchesFilters;
  });

  // Handle single approve
  const handleApprove = async (record) => {
    const token = localStorage.getItem("authToken");

    if (activeTab === "Users") {
      try {
        await axios.post(
          API_CONST.BULK_STATUS_CHANGE,
          { userIds: [record.id], status: "Active" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success(`${record.type} "${record.name}" has been approved and is now active!`);
        fetchPendingUsers();
      } catch (error) {
        message.error(error.response?.data?.message || "Failed to approve user");
      }
    } else if (activeTab === "Profiles") {
      try {
        await axios.patch(
          `${API_CONST.UPDATE_TALENT_STATUS}/${record.id}`,
          { status: "Active" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success(`Talent profile "${record.name}" has been approved and is now active!`);
        fetchPendingTalents();
      } catch (error) {
        message.error(error.response?.data?.message || "Failed to approve talent");
      }
    } else if (activeTab === "Jobs") {
      try {
        await axios.patch(
          `${API_CONST.UPDATE_JOB_STATUS}/${record.id}`,
          { status: "open" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success(`Job post "${record.jobTitle}" has been approved and is now active!`);
        fetchPendingJobs();
      } catch (error) {
        message.error(error.response?.data?.message || "Failed to approve job");
      }
    }
  };

  // Handle bulk approve
  const confirmBulkApprove = () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select items to approve");
      return;
    }
    const tabLabel = activeTab.toLowerCase();
    Modal.confirm({
      title: "Confirm Bulk Approval",
      content: `You are about to approve ${selectedRowKeys.length} ${tabLabel} item${selectedRowKeys.length > 1 ? "s" : ""}. This action cannot be undone.`,
      okText: "Approve",
      okButtonProps: { style: { backgroundColor: "#52c41a", borderColor: "#52c41a" } },
      cancelText: "Cancel",
      onOk: handleBulkApprove,
    });
  };

  const handleBulkApprove = async () => {
    const token = localStorage.getItem("authToken");

    try {
      if (activeTab === "Users") {
        await axios.post(
          API_CONST.BULK_STATUS_CHANGE,
          { userIds: selectedRowKeys, status: "Active" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success(`${selectedRowKeys.length} users approved successfully!`);
        fetchPendingUsers();
      } else if (activeTab === "Profiles") {
        await axios.post(
          API_CONST.BULK_TALENT_STATUS_CHANGE,
          { talentIds: selectedRowKeys, status: "Active" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success(`${selectedRowKeys.length} talent profiles approved successfully!`);
        fetchPendingTalents();
      } else if (activeTab === "Jobs") {
        await Promise.all(
          selectedRowKeys.map((id) =>
            axios.patch(
              `${API_CONST.UPDATE_JOB_STATUS}/${id}`,
              { status: "open" },
              { headers: { Authorization: `Bearer ${token}` } }
            )
          )
        );
        message.success(`${selectedRowKeys.length} jobs approved successfully!`);
        fetchPendingJobs();
      } else if (activeTab === "Engagements") {
        await Promise.all(
          selectedRowKeys.map((id) =>
            axios.post(
              `${API_CONST.APPROVE_ENGAGEMENT}/${id}/approve`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            )
          )
        );
        message.success(`${selectedRowKeys.length} engagements approved successfully!`);
        fetchPendingEngagements();
      } else if (activeTab === "Projects") {
        await Promise.all(
          selectedRowKeys.map((id) =>
            axios.post(
              `${API_CONST.APPROVE_PROJECT}/${id}/approve`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            )
          )
        );
        message.success(`${selectedRowKeys.length} projects approved successfully!`);
        fetchPendingProjects();
      }
      setSelectedRowKeys([]);
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to approve selected items");
    }
  };

  // Handle reject with comment
  const handleRejectClick = (record) => {
    setCurrentRejectRecord(record);
    setRejectModalVisible(true);
  };

  const handleRejectSubmit = async (values) => {
    const { comment } = values;

    const updateData = (data, key) =>
      data.map((item) =>
        item.key === key
          ? { ...item, status: "Rejected", rejectionComment: comment }
          : item
      );

    // Handle Engagement rejection via API
    if (currentRejectRecord?.type === "Engagement") {
      if (!comment || comment.trim().length < 10) {
        message.error("Rejection reason must be at least 10 characters");
        return;
      }
      try {
        const token = localStorage.getItem("authToken");
        await axios.post(
          `${API_CONST.APPROVE_ENGAGEMENT}/${currentRejectRecord.id}/reject`,
          { reason: comment },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success(`Engagement "${currentRejectRecord.title}" has been rejected`);
        fetchPendingEngagements();
      } catch (error) {
        message.error(error.response?.data?.message || "Failed to reject engagement");
      }
    // Handle Project rejection via API  
    } else if (currentRejectRecord?.type === "Project") {
      if (!comment || comment.trim().length < 10) {
        message.error("Rejection reason must be at least 10 characters");
        return;
      }
      try {
        const token = localStorage.getItem("authToken");
        await axios.post(
          `${API_CONST.APPROVE_PROJECT}/${currentRejectRecord.id}/reject`,
          { reason: comment },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success(`Project "${currentRejectRecord.title}" has been rejected`);
        fetchPendingProjects();
      } catch (error) {
        message.error(error.response?.data?.message || "Failed to reject project");
      }
    } else if (activeTab === "Users") {
      try {
        const token = localStorage.getItem("authToken");
        await axios.post(
          API_CONST.BULK_STATUS_CHANGE,
          { userIds: [currentRejectRecord.id], status: "Rejected", rejectionReason: comment || "Rejected by admin" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success(`${currentRejectRecord.type} "${currentRejectRecord.name}" has been rejected`);
        fetchPendingUsers();
      } catch (error) {
        message.error(error.response?.data?.message || "Failed to reject user");
      }
    } else if (activeTab === "Profiles") {
      try {
        const token = localStorage.getItem("authToken");
        await axios.patch(
          `${API_CONST.UPDATE_TALENT_STATUS}/${currentRejectRecord.id}`,
          { status: "Rejected" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success(`Talent profile "${currentRejectRecord.name}" has been rejected`);
        fetchPendingTalents();
      } catch (error) {
        message.error(error.response?.data?.message || "Failed to reject talent");
      }
    } else if (activeTab === "Jobs") {
      try {
        const token = localStorage.getItem("authToken");
        await axios.patch(
          `${API_CONST.UPDATE_JOB_STATUS}/${currentRejectRecord.id}`,
          { status: "rejected" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success(`Job post "${currentRejectRecord.jobTitle}" has been rejected`);
        fetchPendingJobs();
      } catch (error) {
        message.error(error.response?.data?.message || "Failed to reject job");
      }
    }

    setRejectModalVisible(false);
    form.resetFields();
    setCurrentRejectRecord(null);
  };

  // Handle view actions
  const handleViewVerification = async (record) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_CONST.GET_TALENT_PROFILE}/${record.key || record.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const profile = response.data.profile || record;
      const talentData = {
        ...record,
        idVerificationStatus: profile.idVerificationStatus,
        backgroundVerified: profile.backgroundVerified,
        aadhaarNumber: profile.aadhaarNumber,
        panNumber: profile.panNumber,
        passportNumber: profile.passportNumber,
        idVerificationNotes: profile.idVerificationNotes,
        verifiedAt: profile.verifiedAt,
        aadhaarUrl: profile.aadhaarUrl,
        panUrl: profile.panUrl,
        degreeUrl: profile.degreeUrl,
        resumeUrl: profile.resumeUrl,
        summary: profile.summary,
        primarySkills: profile.primarySkills || [],
        secondarySkills: profile.secondarySkills || [],
        skills: profile.skills || [],
        location: profile.location || profile.currentLocation,
        experience: profile.experience || (profile.experienceYears ? `${profile.experienceYears} yrs` : null) || record.experience,
        monthlyRate: (profile.partnerRate || profile.monthlyRate) ? `₹${Number(profile.partnerRate || profile.monthlyRate).toLocaleString("en-IN")}` : record.monthlyRate,
        partnerOrg: profile.partnerOrganization || record.partnerOrg,
      };
      setVerificationTalent(talentData);
      setInlineAadhaarNum(profile.aadhaarNumber || "");
      setInlinePanNum(profile.panNumber || "");
      setInlinePassportNum(profile.passportNumber || "");
      setInlineVerifNotes(profile.idVerificationNotes || "");

      const docUrls = {};
      const docEntries = [
        { key: 'resumeUrl', label: 'resume', path: profile.resumeUrl },
        { key: 'aadhaarUrl', label: 'aadhaar', path: profile.aadhaarUrl },
        { key: 'panUrl', label: 'pan', path: profile.panUrl },
        { key: 'degreeUrl', label: 'degree', path: profile.degreeUrl },
      ];
      await Promise.all(
        docEntries.filter(d => d.path).map(async (d) => {
          try {
            const res = await axios.post(
              API_CONST.GET_DOCUMENT_URL,
              { objectPath: d.path },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            docUrls[d.key] = res.data.downloadUrl || res.data.url;
          } catch {
            docUrls[d.key] = null;
          }
        })
      );
      setVerificationDocUrls(docUrls);
      setVerificationDrawerVisible(true);
    } catch {
      message.error("Failed to load verification details");
    }
  };

  const handleInlineVerify = async (status) => {
    if (!verificationTalent) return;
    setIdVerifLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      await axios.patch(
        `${API_CONST.VERIFY_TALENT}/${verificationTalent.key || verificationTalent.id}`,
        {
          idVerificationStatus: status,
          aadhaarNumber: inlineAadhaarNum || undefined,
          panNumber: inlinePanNum || undefined,
          passportNumber: inlinePassportNum || undefined,
          idVerificationNotes: inlineVerifNotes || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(`ID verification marked as ${status}`);
      setVerificationTalent((prev) => ({ ...prev, idVerificationStatus: status }));
    } catch {
      message.error("Failed to update ID verification status");
    } finally {
      setIdVerifLoading(false);
    }
  };

  const handleView = (record) => {
    if (activeTab === "Users") {
      navigate(`/admin/organization/${encodeURIComponent(record.name)}`, {
        state: { from: "/admin/approval-process", fromLabel: "Back to Approval Process" }
      });
    } else if (activeTab === "Profiles") {
      // Navigate to talent details page (shared talent details component)
      navigate(`/admin/talent-details/${record.key}`);
    } else {
      // Navigate to job details page  
      message.info(`Viewing job details: ${record.jobTitle}`);
    }
  };

  // Create action menu for each row
  const getActionMenu = (record) => {
    // Special handling for Engagements tab
    if (activeTab === "Engagements") {
      return {
        items: [
          {
            key: "view",
            label: "View Details",
            onClick: () => navigate(`/admin/engagements/details/${record.id}`),
          },
          {
            key: "approve",
            label: "Approve",
            onClick: () => handleEngagementApprove(record),
          },
          {
            key: "reject",
            label: "Reject",
            onClick: () => handleEngagementReject(record),
            danger: true,
          },
        ],
      };
    }

    // Special handling for Projects tab
    if (activeTab === "Projects") {
      return {
        items: [
          {
            key: "view",
            label: "View Details",
            onClick: () => navigate(`/admin/projects/details/${record.id}`),
          },
          {
            key: "approve",
            label: "Approve",
            onClick: () => handleProjectApprove(record),
          },
          {
            key: "reject",
            label: "Reject",
            onClick: () => handleProjectReject(record),
            danger: true,
          },
        ],
      };
    }

    // Special handling for Jobs tab
    if (activeTab === "Jobs") {
      return {
        items: [
          {
            key: "view",
            label: "View Details",
            onClick: () => navigate(`/admin/job-requirments/job-full-details/${record.id}`, { state: { from: 'approval-process', tab: 'Jobs' } }),
          },
          {
            key: "edit",
            label: "Edit Job",
            onClick: () => navigate(`/admin/job-requirments/edit-job/${record.id}`, { state: { from: 'approval-process', tab: 'Jobs' } }),
          },
          {
            key: "approve",
            label: "Approve & Activate",
            onClick: () => handleApprove(record),
          },
          {
            key: "reject",
            label: "Reject",
            onClick: () => handleRejectClick(record),
            danger: true,
          },
        ],
      };
    }

    // Special handling for Profiles tab
    if (activeTab === "Profiles") {
      return {
        items: [
          {
            key: "view",
            label: "View Details",
            onClick: () => handleViewVerification(record),
          },
          ...(record.status === "Pending"
            ? [
                {
                  key: "approve",
                  label: "Approve",
                  onClick: () => handleApprove(record),
                },
                {
                  key: "reject",
                  label: "Reject",
                  onClick: () => handleRejectClick(record),
                },
              ]
            : []),
        ],
      };
    }

    // Default handling for other tabs
    return {
      items: [
        {
          key: "view",
          label: "View Details",
          onClick: () => handleView(record),
        },
        ...(record.status === "Pending"
          ? [
              {
                key: "approve",
                label: "Approve",
                onClick: () => handleApprove(record),
              },
              {
                key: "reject",
                label: "Reject",
                onClick: () => handleRejectClick(record),
              },
            ]
          : []),
      ],
    };
  };

  // Columns for Partners/Customers
  const partnersColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 160,
      ellipsis: true,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 100,
      render: (type) => (
        <Tag color={type === "Partner" ? "blue" : "green"}>{type}</Tag>
      ),
    },
    {
      title: "Contact Person",
      dataIndex: "contactPerson",
      key: "contactPerson",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 130,
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 130,
      ellipsis: true,
    },
    {
      title: "Registered On",
      dataIndex: "registeredOn",
      key: "registeredOn",
      width: 120,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag
          color={
            status === "Approved"
              ? "green"
              : status === "Rejected"
              ? "red"
              : "orange"
          }
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Dropdown menu={getActionMenu(record)} trigger={["click"]}>
          <MoreOutlined style={{ cursor: "pointer", fontSize: "18px" }} />
        </Dropdown>
      ),
    },
  ];

  // Columns for Talent Profiles
  const talentsColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 160,
      ellipsis: true,
    },
    {
      title: "Skills",
      dataIndex: "skills",
      key: "skills",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Experience",
      dataIndex: "experience",
      key: "experience",
      width: 110,
      ellipsis: true,
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 130,
      ellipsis: true,
    },
    {
      title: "Monthly Rate",
      dataIndex: "monthlyRate",
      key: "monthlyRate",
      width: 130,
      render: (rate) => rate && rate !== "-" ? <span style={{ fontWeight: 600, color: "#014c75" }}>{rate}</span> : "-",
    },
    {
      title: "Partner Org",
      dataIndex: "partnerOrg",
      key: "partnerOrg",
      width: 160,
      ellipsis: true,
      render: (org) => org ? (
        <a
          style={{ color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/admin/organization/${encodeURIComponent(org)}`, {
            state: { from: "/admin/approval-process", fromLabel: "Back to Approval Process" }
          })}
        >
          {org}
        </a>
      ) : "-",
    },
    {
      title: "Submitted On",
      dataIndex: "submittedOn",
      key: "submittedOn",
      width: 120,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag
          color={
            status === "Approved"
              ? "green"
              : status === "Rejected"
              ? "red"
              : "orange"
          }
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Dropdown menu={getActionMenu(record)} trigger={["click"]}>
          <MoreOutlined style={{ cursor: "pointer", fontSize: "18px" }} />
        </Dropdown>
      ),
    },
  ];

  // Columns for Job Posts
  const jobsColumns = [
    {
      title: "Job Title",
      dataIndex: "jobTitle",
      key: "jobTitle",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
      width: 160,
      ellipsis: true,
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 120,
      ellipsis: true,
    },
    {
      title: "Experience",
      dataIndex: "experience",
      key: "experience",
      width: 110,
      ellipsis: true,
    },
    {
      title: "Skills",
      dataIndex: "skills",
      key: "skills",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Budget Range",
      dataIndex: "budget",
      key: "budget",
      width: 130,
    },
    {
      title: "Positions",
      dataIndex: "positions",
      key: "positions",
      width: 90,
      render: (count) => <Tag color="blue">{count}</Tag>,
    },
    {
      title: "Posted On",
      dataIndex: "postedOn",
      key: "postedOn",
      width: 110,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status) => (
        <Tag
          color={
            status === "Approved"
              ? "green"
              : status === "Rejected"
              ? "red"
              : "orange"
          }
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Dropdown menu={getActionMenu(record)} trigger={["click"]}>
          <MoreOutlined style={{ cursor: "pointer", fontSize: "18px" }} />
        </Dropdown>
      ),
    },
  ];

  // Columns for Engagements
  const engagementsColumns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Customer",
      dataIndex: "customerOrganization",
      key: "customerOrganization",
      width: 180,
      ellipsis: true,
      render: (org) => org ? (
        <a
          style={{ color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/admin/organization/${encodeURIComponent(org)}`, {
            state: { from: "/admin/approval-process", fromLabel: "Back to Approval Process" }
          })}
        >
          {org}
        </a>
      ) : "-",
    },
    {
      title: "Type",
      dataIndex: "engagementType",
      key: "engagementType",
      width: 120,
      render: (type) => <Tag color="blue">{type || "N/A"}</Tag>,
    },
    {
      title: "Total Budget",
      dataIndex: "totalBudget",
      key: "totalBudget",
      width: 150,
      render: (budget) => budget ? `₹${Number(budget).toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 110,
      render: (date) => date ? new Date(date).toLocaleDateString("en-IN") : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <Tag color="orange">Pending Approval</Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Dropdown menu={getActionMenu(record)} trigger={["click"]}>
          <MoreOutlined style={{ cursor: "pointer", fontSize: "18px" }} />
        </Dropdown>
      ),
    },
  ];

  // Columns for Projects
  const projectsColumns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Customer",
      dataIndex: "customerOrganization",
      key: "customerOrganization",
      width: 180,
      ellipsis: true,
      render: (org) => org ? (
        <a
          style={{ color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/admin/organization/${encodeURIComponent(org)}`, {
            state: { from: "/admin/approval-process", fromLabel: "Back to Approval Process" }
          })}
        >
          {org}
        </a>
      ) : "-",
    },
    {
      title: "Engagement",
      dataIndex: "engagementId",
      key: "engagementId",
      width: 150,
      ellipsis: true,
      render: (id) => id ? (
        <a
          style={{ color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/admin/engagements/details/${id}`)}
        >
          {id}
        </a>
      ) : "-",
    },
    {
      title: "Budget",
      dataIndex: "budgetAllocated",
      key: "budgetAllocated",
      width: 120,
      render: (budget) => budget ? `₹${budget.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 110,
      render: (date) => date ? new Date(date).toLocaleDateString("en-IN") : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <Tag color="orange">Pending Approval</Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Dropdown menu={getActionMenu(record)} trigger={["click"]}>
          <MoreOutlined style={{ cursor: "pointer", fontSize: "18px" }} />
        </Dropdown>
      ),
    },
  ];

  // Get columns based on active tab
  const getColumns = () => {
    switch (activeTab) {
      case "Users":
        return partnersColumns;
      case "Profiles":
        return talentsColumns;
      case "Jobs":
        return jobsColumns;
      case "Engagements":
        return engagementsColumns;
      case "Projects":
        return projectsColumns;
      default:
        return [];
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    },
    getCheckboxProps: (record) => {
      // For Engagements/Projects tabs, always enable (they only show pending items)
      // For other tabs, enable selection for "Pending" status
      if (activeTab === "Engagements" || activeTab === "Projects") {
        return {
          disabled: false,
        };
      }
      return {
        disabled: record.status !== "Pending",
      };
    },
  };

  return (
    <ApprovalProcessWrapper>
      <div className="approval-container">
        <PageHeader><h1>Approval Process</h1><p>Review and process pending approvals</p></PageHeader>

        {/* Tabs Section */}
        <div style={{ display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" }}>
          {[
            { key: "Users", count: pendingCounts.partners },
            { key: "Engagements", count: pendingCounts.engagements },
            { key: "Projects", count: pendingCounts.projects },
            { key: "Jobs", count: pendingCounts.jobs },
            { key: "Profiles", count: pendingCounts.talents },
          ].map((tab) => (
            <Button
              key={tab.key}
              style={{
                backgroundColor: activeTab === tab.key ? "#00d9a9" : "#fff",
                color: activeTab === tab.key ? "#fff" : "#014c75",
                border: activeTab === tab.key ? "1px solid #00d9a9" : "1px solid #d9d9d9",
                fontWeight: activeTab === tab.key ? "600" : "500",
                padding: "8px 20px",
                height: "auto",
              }}
              onClick={() => {
                setActiveTab(tab.key);
                setSelectedRowKeys([]);
                setSearchText("");
                setFilterType(null);
                setFilterLocation(null);
                setFilterRole(null);
                setFilterExperience(null);
              }}
            >
              {tab.key} ({tab.count})
            </Button>
          ))}
        </div>

        {/* Timesheets summary — managed in the dedicated Timesheets module */}
        {pendingCounts.timesheets > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#fff7e6",
              border: "1px solid #ffd591",
              borderRadius: "8px",
              padding: "10px 16px",
              marginBottom: "16px",
            }}
          >
            <span style={{ color: "#d46b08", fontWeight: 500 }}>
              ⏱&nbsp; <strong>{pendingCounts.timesheets}</strong> timesheet{pendingCounts.timesheets !== 1 ? "s" : ""} pending approval
            </span>
            <Button
              type="link"
              style={{ color: "#014c75", fontWeight: 600, padding: 0 }}
              onClick={() => navigate("/admin/timesheet")}
            >
              Go to Timesheets →
            </Button>
          </div>
        )}

        {/* Search and Actions Section */}
        <Flex align="start" justify="space-between" style={{ marginBottom: "20px" }}>
          <Flex gap="middle">
            <Input
              prefix={<SearchOutlined />}
              placeholder={`Search ${
                activeTab === "Users"
                  ? "by name, contact person, or email"
                  : activeTab === "Profiles"
                  ? "by name, role, or skills"
                  : activeTab === "Engagements"
                  ? "by ID, title, or customer"
                  : activeTab === "Projects"
                  ? "by ID, title, or customer"
                  : "by job title, company, or skills"
              }`}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: "300px" }}
              allowClear
            />

            {/* Dynamic Filters based on active tab */}
            {activeTab === "Users" && (
              <>
                <Select
                  placeholder={
                    <span>
                      <FilterOutlined style={{ marginRight: 8 }} />
                      Filter by Type
                    </span>
                  }
                  value={filterType}
                  onChange={setFilterType}
                  style={{ width: 200 }}
                  allowClear
                >
                  <Option value="Partner">Partner</Option>
                  <Option value="Customer">Customer</Option>
                </Select>
                <Select
                  placeholder={
                    <span>
                      <FilterOutlined style={{ marginRight: 8 }} />
                      Filter by Location
                    </span>
                  }
                  value={filterLocation}
                  onChange={setFilterLocation}
                  style={{ width: 200 }}
                  allowClear
                >
                  <Option value="Bangalore">Bangalore</Option>
                  <Option value="Mumbai">Mumbai</Option>
                  <Option value="Pune">Pune</Option>
                  <Option value="Hyderabad">Hyderabad</Option>
                </Select>
              </>
            )}

            {activeTab === "Profiles" && (
              <>
                <Select
                  placeholder={
                    <span>
                      <FilterOutlined style={{ marginRight: 8 }} />
                      Filter by Role
                    </span>
                  }
                  value={filterRole}
                  onChange={setFilterRole}
                  style={{ width: 220 }}
                  allowClear
                >
                  <Option value="Senior React Developer">Senior React Developer</Option>
                  <Option value="Full Stack Developer">Full Stack Developer</Option>
                  <Option value="DevOps Engineer">DevOps Engineer</Option>
                  <Option value="UI/UX Designer">UI/UX Designer</Option>
                </Select>
                <Select
                  placeholder={
                    <span>
                      <FilterOutlined style={{ marginRight: 8 }} />
                      Filter by Location
                    </span>
                  }
                  value={filterLocation}
                  onChange={setFilterLocation}
                  style={{ width: 200 }}
                  allowClear
                >
                  <Option value="Bangalore">Bangalore</Option>
                  <Option value="Delhi">Delhi</Option>
                  <Option value="Chennai">Chennai</Option>
                  <Option value="Mumbai">Mumbai</Option>
                </Select>
              </>
            )}

            {activeTab === "Jobs" && (
              <>
                <Select
                  placeholder={
                    <span>
                      <FilterOutlined style={{ marginRight: 8 }} />
                      Filter by Location
                    </span>
                  }
                  value={filterLocation}
                  onChange={setFilterLocation}
                  style={{ width: 200 }}
                  allowClear
                >
                  <Option value="Bangalore">Bangalore</Option>
                  <Option value="Hyderabad">Hyderabad</Option>
                  <Option value="Mumbai">Mumbai</Option>
                </Select>
                <Select
                  placeholder={
                    <span>
                      <FilterOutlined style={{ marginRight: 8 }} />
                      Filter by Experience
                    </span>
                  }
                  value={filterExperience}
                  onChange={setFilterExperience}
                  style={{ width: 200 }}
                  allowClear
                >
                  <Option value="4+ years">4+ years</Option>
                  <Option value="5+ years">5+ years</Option>
                  <Option value="6+ years">6+ years</Option>
                </Select>
              </>
            )}

          </Flex>

          {selectedRowKeys.length > 0 && (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={confirmBulkApprove}
              style={{
                backgroundColor: "#52c41a",
                borderColor: "#52c41a",
              }}
            >
              Bulk Approve ({selectedRowKeys.length})
            </Button>
          )}
        </Flex>

        {/* Table */}
        <Table
          rowSelection={rowSelection}
          columns={getColumns()}
          dataSource={filteredData}
          scroll={{ x: "max-content" }}
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
          loading={
            (activeTab === "Users" && loadingUsers) ||
            (activeTab === "Profiles" && loadingTalents) ||
            (activeTab === "Jobs" && loadingJobs) ||
            (activeTab === "Engagements" && loadingEngagements) ||
            (activeTab === "Projects" && loadingProjects)
          }
        />

        {/* Reject with Comment Modal */}
        <Modal
          title="Reject with Comment"
          open={rejectModalVisible}
          onCancel={() => {
            setRejectModalVisible(false);
            form.resetFields();
            setCurrentRejectRecord(null);
          }}
          footer={null}
          width={500}
        >
          <Form
            form={form}
            onFinish={handleRejectSubmit}
            layout="vertical"
            style={{ marginTop: "20px" }}
          >
            <Form.Item
              label="Reason for Rejection"
              name="comment"
              rules={[
                {
                  required: true,
                  message: "Please provide a reason for rejection",
                },
                {
                  min: 10,
                  message: "Reason must be at least 10 characters long",
                },
              ]}
            >
              <TextArea
                rows={4}
                placeholder="Enter detailed reason for rejection (minimum 10 characters)"
                maxLength={500}
                showCount
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
              <Space>
                <Button
                  onClick={() => {
                    setRejectModalVisible(false);
                    form.resetFields();
                    setCurrentRejectRecord(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  danger
                  icon={<CloseCircleOutlined />}
                >
                  Reject
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Talent Details Drawer */}
        <Drawer
          title={
            <Space>
              <CheckCircleOutlined style={{ color: "#014c75" }} />
              {verificationTalent?.name} — Profile Details
            </Space>
          }
          open={verificationDrawerVisible}
          onClose={() => {
            setVerificationDrawerVisible(false);
            setVerificationTalent(null);
            setVerificationDocUrls({});
          }}
          width={660}
          extra={
            verificationTalent?.status !== "Pending" && verificationTalent?.idVerificationStatus !== "Verified" ? (
              <Button
                type="link"
                icon={<SafetyCertificateOutlined />}
                onClick={() => {
                  setVerificationDrawerVisible(false);
                  setVerificationTalent(null);
                  navigate(`/admin/talent-profiles/documents/${verificationTalent.key || verificationTalent.id}`);
                }}
              >
                Open Full Verification →
              </Button>
            ) : null
          }
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <Button
                onClick={() => {
                  setVerificationDrawerVisible(false);
                  setVerificationTalent(null);
                  setVerificationDocUrls({});
                }}
              >
                Close
              </Button>
              {verificationTalent?.status === "Pending" && (
                <>
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={() => {
                      setVerificationDrawerVisible(false);
                      handleRejectClick(verificationTalent);
                    }}
                  >
                    Reject
                  </Button>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    style={{ background: "#52c41a", borderColor: "#52c41a" }}
                    onClick={async () => {
                      await handleApprove(verificationTalent);
                      setVerificationDrawerVisible(false);
                      setVerificationTalent(null);
                      setVerificationDocUrls({});
                    }}
                  >
                    Approve Profile
                  </Button>
                </>
              )}
            </div>
          }
        >
          {verificationTalent && (
            <div>
              {/* ── Profile Header ── */}
              <div style={{ display: "flex", alignItems: "center", gap: 14, background: "#f8f9fd", borderRadius: 8, padding: "14px 16px", marginBottom: 18, border: "1px solid #e8e8e8" }}>
                <Avatar size={52} style={{ background: "linear-gradient(135deg,#014c75,#00d9a9)", fontSize: 22, flexShrink: 0 }}>
                  {verificationTalent.name?.charAt(0) || "?"}
                </Avatar>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: "#014c75", fontSize: 16 }}>{verificationTalent.name}</div>
                  <div style={{ color: "#666", fontSize: 13, marginTop: 2 }}>
                    {verificationTalent.role}
                    {verificationTalent.partnerOrg ? <span style={{ color: "#bbb", margin: "0 6px" }}>•</span> : ""}
                    {verificationTalent.partnerOrg && <span style={{ color: "#888" }}>{verificationTalent.partnerOrg}</span>}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                    {verificationTalent.idVerificationStatus === "Verified" && <Tag color="success" icon={<SafetyCertificateOutlined />}>ID Verified</Tag>}
                    {verificationTalent.idVerificationStatus === "Failed" && <Tag color="error" icon={<ExclamationCircleOutlined />}>ID Failed</Tag>}
                    {(!verificationTalent.idVerificationStatus || verificationTalent.idVerificationStatus === "Pending") && <Tag color="warning" icon={<ClockCircleOutlined />}>ID Pending</Tag>}
                    {verificationTalent.backgroundVerified === "Yes" && <Tag color="success">BGV ✓</Tag>}
                  </div>
                </div>
              </div>

              {/* ── Profile Details Grid ── */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 24px", marginBottom: 16 }}>
                {verificationTalent.location && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Location</Text>
                    <div style={{ marginTop: 3, fontWeight: 500, fontSize: 13, display: "flex", alignItems: "center", gap: 4 }}>
                      <EnvironmentOutlined style={{ color: "#1890ff" }} /> {verificationTalent.location}
                    </div>
                  </div>
                )}
                {verificationTalent.experience && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Experience</Text>
                    <div style={{ marginTop: 3, fontWeight: 500, fontSize: 13 }}>{verificationTalent.experience}</div>
                  </div>
                )}
                {verificationTalent.monthlyRate && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Monthly Rate</Text>
                    <div style={{ marginTop: 3, fontWeight: 600, fontSize: 13, color: "#014c75", display: "flex", alignItems: "center", gap: 4 }}>
                      <DollarOutlined style={{ color: "#00d9a9" }} /> {verificationTalent.monthlyRate}
                    </div>
                  </div>
                )}
                {verificationTalent.submittedOn && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Submitted On</Text>
                    <div style={{ marginTop: 3, fontWeight: 500, fontSize: 13 }}>{verificationTalent.submittedOn}</div>
                  </div>
                )}
              </div>

              {/* ── Skills ── */}
              {(verificationTalent.primarySkills?.length > 0 || verificationTalent.skills?.length > 0) && (
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>Skills</Text>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {(verificationTalent.primarySkills?.length > 0
                      ? verificationTalent.primarySkills
                      : (typeof verificationTalent.skills === "string"
                          ? verificationTalent.skills.split(",").map(s => s.trim())
                          : verificationTalent.skills)
                    ).filter(Boolean).map((s, i) => (
                      <Tag key={i} color="cyan" style={{ fontSize: 12 }}>{s}</Tag>
                    ))}
                    {verificationTalent.secondarySkills?.filter(Boolean).map((s, i) => (
                      <Tag key={`s${i}`} color="blue" style={{ fontSize: 12 }}>{s}</Tag>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Summary ── */}
              {verificationTalent.summary && (
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>Professional Summary</Text>
                  <div style={{ padding: "8px 12px", background: "#fafafa", borderRadius: 6, border: "1px solid #f0f0f0", fontSize: 13, color: "#555", lineHeight: 1.6 }}>
                    {verificationTalent.summary}
                  </div>
                </div>
              )}

              <Divider style={{ margin: "14px 0" }} />

              {/* ── ID Verification Section ── */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <Text strong style={{ fontSize: 14, color: "#014c75" }}>
                    <SafetyCertificateOutlined style={{ marginRight: 6 }} />
                    ID Verification
                  </Text>
                  <div>
                    {verificationTalent.idVerificationStatus === "Verified" ? (
                      <Tag color="success" icon={<CheckCircleOutlined />}>Verified</Tag>
                    ) : verificationTalent.idVerificationStatus === "Failed" ? (
                      <Tag color="error" icon={<ExclamationCircleOutlined />}>Failed</Tag>
                    ) : (
                      <Tag color="warning" icon={<ClockCircleOutlined />}>Pending</Tag>
                    )}
                  </div>
                </div>

                {/* ID Number Inputs */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px", marginBottom: 12 }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Aadhaar Number</Text>
                    <Input
                      size="small"
                      placeholder="e.g. 1234 5678 9012"
                      value={inlineAadhaarNum}
                      onChange={(e) => setInlineAadhaarNum(e.target.value)}
                      maxLength={20}
                      style={{ fontFamily: "monospace" }}
                    />
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>PAN Number</Text>
                    <Input
                      size="small"
                      placeholder="e.g. ABCDE1234F"
                      value={inlinePanNum}
                      onChange={(e) => setInlinePanNum(e.target.value)}
                      maxLength={20}
                      style={{ fontFamily: "monospace", textTransform: "uppercase" }}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Passport Number (optional)</Text>
                    <Input
                      size="small"
                      placeholder="e.g. A1234567"
                      value={inlinePassportNum}
                      onChange={(e) => setInlinePassportNum(e.target.value)}
                      maxLength={40}
                      style={{ fontFamily: "monospace" }}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Verification Notes</Text>
                    <Input.TextArea
                      size="small"
                      rows={2}
                      placeholder="Notes about verification (optional)"
                      value={inlineVerifNotes}
                      onChange={(e) => setInlineVerifNotes(e.target.value)}
                      maxLength={300}
                    />
                  </div>
                </div>

                {/* Verification Action Buttons */}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Button
                    size="small"
                    type="primary"
                    icon={<SafetyCertificateOutlined />}
                    loading={idVerifLoading}
                    disabled={verificationTalent.idVerificationStatus === "Verified"}
                    onClick={() => handleInlineVerify("Verified")}
                    style={{ background: "#52c41a", borderColor: "#52c41a" }}
                  >
                    Mark ID Verified
                  </Button>
                  <Button
                    size="small"
                    danger
                    icon={<ExclamationCircleOutlined />}
                    loading={idVerifLoading}
                    disabled={verificationTalent.idVerificationStatus === "Failed"}
                    onClick={() => handleInlineVerify("Failed")}
                  >
                    Mark ID Failed
                  </Button>
                  {verificationTalent.idVerificationStatus !== "Pending" && (
                    <Button
                      size="small"
                      icon={<ClockCircleOutlined />}
                      loading={idVerifLoading}
                      onClick={() => handleInlineVerify("Pending")}
                    >
                      Reset to Pending
                    </Button>
                  )}
                </div>

                {verificationTalent.verifiedAt && verificationTalent.idVerificationStatus === "Verified" && (
                  <div style={{ marginTop: 8, fontSize: 12, color: "#999" }}>
                    Verified on {new Date(verificationTalent.verifiedAt).toLocaleDateString("en-IN")}
                  </div>
                )}
              </div>

              <Divider style={{ margin: "14px 0" }} />

              {/* ── Documents ── */}
              <div style={{ marginBottom: 8 }}>
                <Text strong style={{ fontSize: 14, color: "#014c75", display: "block", marginBottom: 10 }}>
                  <FilePdfOutlined style={{ marginRight: 6 }} />
                  Documents
                </Text>
                <Collapse
                  size="small"
                  items={[
                    { label: "Resume", urlKey: "resumeUrl", hasDoc: !!verificationTalent.resumeUrl },
                    { label: "Aadhaar / ID Document", urlKey: "aadhaarUrl", hasDoc: !!verificationTalent.aadhaarUrl },
                    { label: "PAN Card", urlKey: "panUrl", hasDoc: !!verificationTalent.panUrl },
                    { label: "Degree Certificate", urlKey: "degreeUrl", hasDoc: !!verificationTalent.degreeUrl },
                  ]
                    .filter(d => d.hasDoc)
                    .map(d => ({
                      key: d.urlKey,
                      label: (
                        <Space>
                          <FilePdfOutlined style={{ color: "#014c75" }} />
                          {d.label}
                          {verificationDocUrls[d.urlKey] ? (
                            <Tag color="success" style={{ fontSize: 11 }}>Ready</Tag>
                          ) : (
                            <Tag color="default" style={{ fontSize: 11 }}>Loading...</Tag>
                          )}
                        </Space>
                      ),
                      children: verificationDocUrls[d.urlKey] ? (
                        <div>
                          <div style={{ textAlign: "right", marginBottom: 8 }}>
                            <a href={verificationDocUrls[d.urlKey]} target="_blank" rel="noopener noreferrer">
                              <Button size="small" type="link">Open in new tab ↗</Button>
                            </a>
                          </div>
                          <iframe
                            src={verificationDocUrls[d.urlKey]}
                            title={d.label}
                            width="100%"
                            height="320"
                            style={{ border: "1px solid #d9d9d9", borderRadius: 4 }}
                          />
                        </div>
                      ) : (
                        <div style={{ textAlign: "center", padding: 20, color: "#999" }}>
                          <LoadingOutlined spin style={{ marginRight: 8 }} />
                          Loading preview...
                        </div>
                      ),
                    }))}
                />
                {!verificationTalent.resumeUrl && !verificationTalent.aadhaarUrl && !verificationTalent.panUrl && !verificationTalent.degreeUrl && (
                  <div style={{ color: "#999", fontSize: 13, padding: "8px 0" }}>No documents uploaded yet</div>
                )}
              </div>
            </div>
          )}
        </Drawer>
      </div>
    </ApprovalProcessWrapper>
  );
};

export default ApprovalProcess;
